import { ProcessGeneral } from 'api/endpoints/processes';
import BaseRepo from 'container/BaseRepo';
import { buildBadRequestApiError } from 'container/helpers/errors';
import { ApiResponseAsync, isApiError } from 'container/models';
import { SearchRequestTyped } from 'container/search';

import {
  CreateDriverSettlementsScenarioRequest,
  DriverSettlementsScenario,
  DriverSettlementsScenarioRequestType,
  DriverSettlementsScenarioWithHypermedia,
  IDriverSettlementsScenariosRepo,
} from './IDriverSettlementsScenarioRepo';

export function hasDriverSettlementsLicense(draft?: ProcessGeneral) {
  return !!draft?._links?.['searchDriverSettlementsScenarios'];
}

export class DriverSettlementsScenarioRepo
  extends BaseRepo
  implements IDriverSettlementsScenariosRepo
{
  create = async (
    draft: ProcessGeneral,
    request: CreateDriverSettlementsScenarioRequest
  ): ApiResponseAsync<DriverSettlementsScenarioWithHypermedia> => {
    const link = draft._links?.['createDriverSettlementsScenario'];
    if (!link) {
      return buildBadRequestApiError(
        new Error(
          'link "create Driver Settlements Scenario" is not available in process draft'
        )
      );
    }

    return this.helpers.handlePost(link.href, request);
  };

  delete = (
    scenario: DriverSettlementsScenarioWithHypermedia
  ): ApiResponseAsync<void> => {
    const link = scenario._links ? scenario._links['delete'] : undefined;
    if (!link)
      return Promise.resolve(
        buildBadRequestApiError(new Error('link "delete" is not available'))
      );

    return this.helpers.handleDelete(link.href);
  };

  search = async (
    draft: ProcessGeneral,
    request: SearchRequestTyped<DriverSettlementsScenario>
  ): ApiResponseAsync<Array<DriverSettlementsScenarioWithHypermedia>> => {
    const link = draft._links?.['searchDriverSettlementsScenarios'];

    if (!link) {
      return buildBadRequestApiError(
        new Error('link "search Driver Settlements scenarios" is not available')
      );
    }

    const searchResult = await this.helpers.executeSearch<
      DriverSettlementsScenarioWithHypermedia[]
    >(link, request, { convertRequestToSnakeCase: true });
    if (isApiError(searchResult)) {
      return searchResult;
    }
    return searchResult ?? [];
  };

  setActive = async (
    scenario: DriverSettlementsScenarioWithHypermedia,
    activate: boolean
  ): ApiResponseAsync<DriverSettlementsScenarioWithHypermedia> => {
    const linkName = activate ? 'activate' : 'deactivate';
    const link = scenario._links?.[linkName];

    if (!link)
      return buildBadRequestApiError(
        new Error(`link "${linkName}" is not available`)
      );

    return this.helpers.handlePost(link.href, undefined);
  };

  update = async (
    scenario: DriverSettlementsScenarioWithHypermedia,
    request: DriverSettlementsScenarioRequestType
  ): ApiResponseAsync<DriverSettlementsScenarioWithHypermedia> => {
    const link = scenario._links ? scenario._links['update'] : undefined;
    if (!link)
      return buildBadRequestApiError(
        new Error('link "update" is not available')
      );

    return this.helpers.executePatch(link, request);
  };
}
